'use client'

import { fetchAutosuggestAction } from '@/app/actions/autosuggest'
import { ApiMode } from 'autosuggest-sdk'
import Button from '@/app/components/common/Button'
import Popup from '@/app/components/common/Popup'
import FormInput from '@/app/components/Input'
import SearchAutosuggest from '@/app/components/SearchAutosuggest'
import {
	AvailableInputTypes,
	COOKIE_NOTICE_STORAGE_KEY,
	DATE_FORMAT_QUERY,
	DEFAULT_ALLOCATION,
	DEFAULT_CURRENCY,
	defaultCheckInDate,
	defaultCheckOutDate,
	FORM_TYPE,
	INIT_DATA_STORAGE_KEY,
	INPUT,
	LG_BREAKPOINT,
	MD_BREAKPOINT,
	PAGE_TYPE,
	REDIRECT_FORM_FRONT_LOCAL_STORAGE_KEY,
	REDIRECT_PARTNERS,
	ROOM_GUESTS_SEPARATOR,
	SM_BREAKPOINT,
	SM_HEIGHT_BREAKPOINT,
} from '@/app/constants'
import {
	Allocation,
	SanityData,
	SearchItem,
	SearchItemLocationType,
	Suggestion,
	UserData,
} from '@/app/types'
import {
	checkDate,
	getFormData,
	getGuestsCount,
	getLocaleByCode,
	getRedirectPartnerInfo,
	getComparisonCheckboxText,
	parseGooglePlaceToSelectedItem,
	parseSearchParamsToLower,
	scrollToSelectedMonth,
	smoothScrollTo,
	submitHiddenForm,
	validateDateRange,
	validateEmail
} from '@/app/utils'
import { buildRedirectUrl, isKayakMetaFlow } from '@/app/utils/kayakRedirect'
import { buildSearchDestinationPath } from '@/app/utils/build-search-destination-path'
import {
	normalizeBackPartnerParams,
	normalizeFrontPartnerParams,
	normalizePartnerParams,
} from '@/app/utils/partner-mapping'
import { useLocalStorage, useMediaQuery } from '@uidotdev/usehooks'
import classNames from 'classnames'
import { addDays, addMonths, addWeeks, format, isValid } from 'date-fns'
import { camelCase, find, kebabCase, startCase } from 'lodash'
import { useTranslations } from 'next-intl'
import { useParams, useRouter, useSearchParams } from 'next/navigation'
import { FC, FormEvent, ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import {
	SuggestionSelectedEventData,
	SuggestionsFetchRequestedParams,
} from 'react-autosuggest'

import { priceAlertSubscription, type PriceAlertSubscriptionData } from '@/app/api/subscribe'
import BookingPlaceholder from '@/app/components/BookingPlaceholder'
import Checkbox from '@/app/components/common/Checkbox'
import EmailSignup from '@/app/components/EmailSignup'
import { DisplayData } from '@/app/components/LocationDataHandler'
import { Icons } from '@/app/components/ui/Icons'

// Dynamic imports for heavy components that are only needed on user interaction
const BookingDatepicker = dynamic(
	() => import('@/app/components/BookingDatepicker'),
	{ ssr: false }
)
const GuestsSelect = dynamic(
	() => import('@/app/components/GuestsSelect'),
	{ ssr: false }
)
const FullScreenDrawer = dynamic(
	() => import('@/app/components/common/FullScreenDrawer'),
	{ ssr: false }
)
import eventBus from '@/app/eventBus'
import {
	emitFormEmailSubmitGAEvent,
	emitFormEmailSubscribeBounceGAEvent,
	emitFormSearchGAEvent,
	emitFromDateGAEvent,
	emitGuestsClickGAEvent,
	emitGuestsSelectGAEvent,
	emitSearchWithoutLocationGAEvent,
	emitSignUpDealsGAEvent,
	emitToDateGAEvent,
} from '@/app/ga-event-helpers'
import { PS_EVENTS } from '@/app/ps-events'
import { useFeatureIsOn } from '@growthbook/growthbook-react'
import * as Sentry from '@sentry/nextjs'
import dynamic from 'next/dynamic'
import { RemoveScroll } from 'react-remove-scroll'
import useGoogleSignin from '../hooks/useGoogleSignin'
import { useFeatureFlags, FeatureFlag } from '../hooks/useFeatureFlags'
import { countryLanguageMap } from '@/i18n/languageMapping'
import { getKayakLanguageCode } from '@/app/utils/kayakRedirect'
import { parseStayDateFromUrlOrLegacy } from '@/app/utils/calendar-date'

export interface Props {
	displayValue?: DisplayData
	defaultLocation?: SearchItem | null
	isMainPage?: boolean
	isHotelLandingPage?: boolean
	sanityData?: SanityData
	loading?: boolean
	isHotelPage?: boolean
	customHeading?: boolean
	hotel?: any
	isExpanded?: boolean
	onClose?: () => void
	mobileHeadingSlot?: ReactNode
}

const Booking: FC<Props> = ({
	displayValue,
	defaultLocation,
	isMainPage,
	sanityData,
	isHotelLandingPage,
	loading,
	onClose,
	hotel,
	isHotelPage,
	isExpanded,
	customHeading = false,
	mobileHeadingSlot,
}) => {
	const router = useRouter()
	const searchParams = useSearchParams()
	const urlSearchParams = new URLSearchParams(searchParams.toString())
	const urlParams = useParams()
	const [_, setFrontFormData] = useLocalStorage(
		REDIRECT_FORM_FRONT_LOCAL_STORAGE_KEY
	)
	const [userData, setUserData] = useLocalStorage<UserData>(
		INIT_DATA_STORAGE_KEY
	)
	const [_cookie, setShowCookieNotice] = useLocalStorage(
		COOKIE_NOTICE_STORAGE_KEY
	)
	const newLogin = useFeatureIsOn("bhp-sign-up");
	const authData = useGoogleSignin(newLogin && isMainPage);

	const getValidDate = (paramName: string, defaultDate: Date) => {
		const dateParam = searchParams.get(paramName)
		if (dateParam && checkDate(dateParam)) {
			const d = parseStayDateFromUrlOrLegacy(dateParam)
			return isValid(d) ? d : null
		}
		return null
	}

	const checkInDate = getValidDate('check-in-date', defaultCheckInDate)
	const checkOutDate = getValidDate('check-out-date', defaultCheckOutDate)

	const lowerCaseSearchParams = parseSearchParamsToLower(searchParams)
	const destCode = lowerCaseSearchParams?.destcode
	const preopenCalendar = lowerCaseSearchParams?.['cal']
	const shouldPreopenCalendar = preopenCalendar === '1'
	const roomsInputRef = useRef<HTMLDivElement>(null)
	const bookingRef = useRef<HTMLDivElement>(null)
	const redirectFormRef = useRef<HTMLFormElement>(null)
	const redirectFormBlankRef = useRef<HTMLFormElement>(null)
	const bookingInputsRef = useRef<HTMLDivElement>(null)
	const bookingInputDestRef = useRef<HTMLInputElement>(null)
	const bookingInputGuestsRef = useRef<HTMLInputElement>(null)
	const selectedItemRef = useRef<SearchItem | null>(null)
	const lastTypedQueryRef = useRef('')
	const lastFetchQueryRef = useRef('')
	const englishLocationNameRef = useRef<string | null>(null)
	const isDesktop = useMediaQuery(LG_BREAKPOINT)
	const isMd = useMediaQuery(MD_BREAKPOINT)
	const isMobile = useMediaQuery(SM_BREAKPOINT)
	const smHeight = useMediaQuery(SM_HEIGHT_BREAKPOINT)
	const [suggestionsResult, setSuggestionsResult] = useState<SearchItem[]>([])
	const [suggestions, setSuggestions] = useState<Suggestion[]>([])
	const [selectedSuggestion, setSelectedSuggestion] = useState<string>()
	const [selectedItem, setSelectedItem] = useState<SearchItem | null>(null)
	const [searchValue, setSearchValue] = useState<DisplayData>()
	const [openedInput, setOpenedInput] = useState<AvailableInputTypes>(null)
	const t = useTranslations('Form')
	const locale = getLocaleByCode(urlParams.lang as string)
	const [dateFrom, setDateFrom] = useState<Date | null>(checkInDate || null)
	const [dateTo, setDateTo] = useState<Date | null>(checkOutDate || null)
	const [roomsAndGuests, setRoomsAndGuests] = useState<Allocation[]>([
		DEFAULT_ALLOCATION,
	])
	const [roomsAndGuestsText, setRoomsAndGuestsText] = useState<string>(
		t('roomsAndGuests', { roomsCount: 1, guestsCount: 2 })
	)
	const [previousDateFrom, setPreviousDateFrom] = useState<Date | null>(null)
	const [previousDateTo, setPreviousDateTo] = useState<Date | null>(null)
	const [isDatepickerOpen, setIsDatepickerOpen] = useState(false)
	const [datesAreChanged, setDatesAreChanged] = useState(false)
	const [inputError, setInputError] = useState<string | null>(null)
	const {
		backTabPartner,
		frontTabPartner,
		hasBackTabRedirect,
		hasFrontPartner,
		isPartnerBooking,
		partnerInfo,
	} = getRedirectPartnerInfo(urlSearchParams)
	const shouldIgnoreFrontTab = frontTabPartner === '0'
	// Initialize checkbox checked state - checked by default when partner search is available
	const [isCompareChecked, setIsCompareChecked] = useState(hasBackTabRedirect && partnerInfo ? true : isPartnerBooking)
	const [userEmail, setUserEmail] = useState('')
	const [emailError, setEmailError] = useState<string>('')
	const [isOpenEmailSignup, setIsOpenEmailSignup] = useState(false)
	// const isFeatureOn = useFeatureIsOn('google_place')
	// const featureOnRef = useRef(false)

	// Compute checkbox text once for consistency
	const checkboxText = partnerInfo ? getComparisonCheckboxText(partnerInfo, isPartnerBooking, t) : null

	// Extract checkbox visibility conditions for better readability
	const shouldShowComparisonCheckbox = hasBackTabRedirect && isMainPage && !isHotelLandingPage && partnerInfo && checkboxText
	const shouldHideComparisonCheckbox = !shouldShowComparisonCheckbox

	// Autosuggest SDK integration
	const requestIdRef = useRef(0)
	const { isEnabled: isFeatureEnabled } = useFeatureFlags()

	const getAutosuggestMode = useCallback((): ApiMode => {
		// Check URL parameter override first (for QA testing)
		const useOldApiParam = searchParams.get('use_old_api')
		if (useOldApiParam === 'true') {
			return 'deprecated'
		}
		if (useOldApiParam === 'false') {
			return 'new'
		}
		// Check feature flag
		const newApiEnabled = isFeatureEnabled(FeatureFlag.NEW_AUTOCOMPLETE_API, urlParams.lang as string)
		return newApiEnabled ? 'new' : 'deprecated'
	}, [searchParams, isFeatureEnabled, urlParams.lang])

	useEffect(() => {
		const handleInputFocus = (inputType: AvailableInputTypes) => {
			setOpenedInput(inputType)

			if (inputType === INPUT.LOCATION && bookingInputDestRef.current) {
				setTimeout(() => bookingInputDestRef.current?.focus(), 0)
			} else if (inputType === INPUT.CALENDAR_FROM) {
				setTimeout(() => {
					openCalendar(INPUT.CALENDAR_FROM)
				}, 0)
			} else if (inputType === INPUT.GUESTS_SELECT && roomsInputRef.current) {
				setTimeout(() => roomsInputRef.current?.focus(), 0)
			}
		}

		const handleFieldError = () => {
			setTimeout(() => {
				setInputError(t('locationError'))
			}, 0)
		}

		const subscriptions = [
			eventBus.subscribe(PS_EVENTS.INPUT_FOCUS, handleInputFocus),
			eventBus.subscribe(PS_EVENTS.FIELD_ERROR, handleFieldError),
			eventBus.subscribe(PS_EVENTS.HANDLE_SEARCH, handleSearch),
		]

		return () => {
			subscriptions.forEach((unsub) => unsub())
		}
	}, [handleSearch])

	// useEffect(() => {
	// 	if (isFeatureOn) {
	// 		featureOnRef.current = true
	// 	}
	// }, [isFeatureOn])

	useEffect(() => {
		if (displayValue) {
			setSearchValue(displayValue)
		}
	}, [displayValue])

	// Update checkbox checked state when URL params change - keep checked by default for partner search
	useEffect(() => {
		setIsCompareChecked(hasBackTabRedirect && partnerInfo ? true : isPartnerBooking)
	}, [hasBackTabRedirect, partnerInfo, isPartnerBooking])

	useEffect(() => {
		if (defaultLocation) {
			setSelectedItem(defaultLocation)

			if (!selectedItemRef.current) {
				selectedItemRef.current = defaultLocation
			}
		}
	}, [defaultLocation])

	useEffect(() => {
		const checkInParam = searchParams.get('check-in-date')
		const checkOutParam = searchParams.get('check-out-date')
		const hadDateParams = !!(checkInParam || checkOutParam)

		const isCheckInValid = !!(checkInParam && checkDate(checkInParam))
		const isCheckOutValid = !!(checkOutParam && checkDate(checkOutParam))

		let needFallback = !isCheckInValid || !isCheckOutValid

		if (!needFallback && checkInParam && checkOutParam) {
			try {
				const inDate = parseStayDateFromUrlOrLegacy(checkInParam)
				const outDate = parseStayDateFromUrlOrLegacy(checkOutParam)
				if (isValid(inDate) && isValid(outDate) && inDate < outDate) {
					setDateFrom(inDate)
					setDateTo(outDate)
					setPreviousDateFrom(inDate)
					setPreviousDateTo(outDate)
				} else {
					needFallback = true
				}
			} catch {
				needFallback = true
			}
		}

		if (needFallback) {
			const isHotel =
				selectedItem?.loctype === SearchItemLocationType.HOTEL ||
				!!isHotelLandingPage

			const fallbackInDate = addWeeks(new Date(), 1)
			let fallbackOutDate = addWeeks(new Date(), 2)

			if (isHotel) {
				fallbackOutDate = addDays(fallbackInDate, 2)
			}

			setDateFrom(fallbackInDate)
			setDateTo(fallbackOutDate)
			setPreviousDateFrom(fallbackInDate)
			setPreviousDateTo(fallbackOutDate)

			const formattedIn = format(fallbackInDate, DATE_FORMAT_QUERY)
			const formattedOut = format(fallbackOutDate, DATE_FORMAT_QUERY)

			if (hadDateParams && (checkInParam !== formattedIn || checkOutParam !== formattedOut)) {
				updateURL(formattedIn, formattedOut)
			}
		}
	}, [searchParams, selectedItem?.loctype, isMainPage, isHotelLandingPage])

	const updateURL = useCallback(
		(newCheckIn: string, newCheckOut: string) => {
			const current = new URLSearchParams(Array.from(searchParams.entries()))
			current.set('check-in-date', newCheckIn)
			current.set('check-out-date', newCheckOut)

			const search = current.toString()
			const query = search ? `?${search}` : ''
			const path = typeof window !== 'undefined' ? window.location.pathname : ''
			router.replace(`${path}${query}`, { scroll: false })
		},
		[router, searchParams]
	)

	const calendarOpenedRef = useRef(false)

	useEffect(() => {
		//cal=1 logic should be ignored on single hotel page
		if (isHotelPage) return

		// Only attempt to open the calendar once when the component mounts
		if (shouldPreopenCalendar && !calendarOpenedRef.current) {
			calendarOpenedRef.current = true

			if (isMainPage) {
				openCalendar(INPUT.CALENDAR_FROM)
			} else {
				if (!isExpanded) {
					const inputType = isDesktop ? INPUT.CALENDAR_FROM : INPUT.MAIN_FORM
					eventBus.publish(PS_EVENTS.INPUT_FOCUS, inputType)
				} else if (isDesktop) {
					setTimeout(() => {
						openCalendar(INPUT.CALENDAR_FROM)
					}, 300)
				}
			}
		}
	}, [shouldPreopenCalendar, isMainPage, isExpanded, isHotelPage])

	useEffect(() => {
		eventBus.publish(PS_EVENTS.SELECTED_ITEM_CHANGED, {
			hasSelectedItem: !!selectedItem,
			selectedItem: selectedItem,
		})
		if (selectedItem) {
			let formData
			const pt = searchParams.get('pt') || null

			if (
				selectedItem.loctype === SearchItemLocationType.HOTEL ||
				(isHotelPage && hasBackTabRedirect && isPartnerBooking)
			) {
				const buid = searchParams.get('buid') || null

				formData = getFormData({
					...selectedItem,
					dateFrom,
					dateTo,
					roomsAndGuests,
					backTabPartner,
					buid,
					pt,
					formType: FORM_TYPE.BACK,
					displayname: `${selectedItem.name}, ${selectedItem.cityonly}, ${selectedItem.country}`,
				})
			} else {
				const hasCityDestcode = destCode?.toLowerCase()?.includes('c:')
				const localDestcode = destCode?.includes(':')
					? destCode.substring(destCode.indexOf(':') + 1)
					: destCode

				formData = getFormData({
					...selectedItem,
					kayakType: 'city',
					// Use placeID for ptid (not ctid) - placeID maps to Kayak's p{ID} format
					...(selectedItem.placeID && { ptid: `${selectedItem.placeID}` }),
					displayname: selectedItem.cityname,
					dateFrom,
					dateTo,
					roomsAndGuests,
					formType: FORM_TYPE.FRONT,
					locationname: selectedItem.name,
					pt,
					...(hasCityDestcode && { ptid: localDestcode }),
				})
			}

			setFrontFormData(formData)
		}
	}, [selectedItem, dateFrom, dateTo, roomsAndGuests])

	useEffect(() => {
		if (searchParams.has('room')) {
			let roomParam: string[] = searchParams.getAll('room')

			if (!Array.isArray(roomParam)) {
				roomParam = [roomParam]
			}

			const rooms = roomParam.map((room: string) => {
				const [adults, children = ''] = room.split(ROOM_GUESTS_SEPARATOR)
				const childAges = children
					? children.split(',').map((age) => Number(age))
					: []

				return {
					adults: parseInt(adults) || 1, // Default to 1 adult if parsing fails
					childAge: childAges,
				}
			})
			handleRoomsAndGuestsChange(rooms)
		}
	}, [searchParams])

	const fetchSuggestions = useCallback(
		async (query: string) => {
			lastFetchQueryRef.current = query
			const currentRequestId = ++requestIdRef.current
			try {
				const lang = urlParams.lang as string
				const mode = getAutosuggestMode()
				const country = countryLanguageMap[lang] || lang.toUpperCase()
				const language = getKayakLanguageCode(lang)
				const autosuggestLocale = mode === 'new' ? language : lang

				const response = await fetchAutosuggestAction({
					query,
					locale: autosuggestLocale,
					language,
					country,
					mode,
				})

				// Discard stale responses
				if (currentRequestId !== requestIdRef.current) return

				if (response.success && response.data) {
					const result = response.data as SearchItem[]
					setSuggestionsResult(result)

					const suggestions = result.map((item) => {
						let itemType = item.loctype
						if (item.kayakType === 'hood' || item.kayakType === 'borough') {
							itemType = SearchItemLocationType.HOOD
						} else if (item.kayakType === 'island') {
							itemType = SearchItemLocationType.REGION
						} else if (item.kayakType === 'touristregion') {
							itemType = SearchItemLocationType.TOURISTREGION
						}
						return {
							id: item.id,
							title:
								item.loctype === SearchItemLocationType.HOTEL
									? item.hotelname
									: item.displayname,
							type: itemType,
							image: item.destination_images?.image_webp,
							locationName: item.locationname,
							displayName: item.displayname,
							placeID: item.placeID,
							country: item.country,
							cityonly: item.cityonly,
						}
					})

					setSuggestions(suggestions)
				} else {
					console.error('Autosuggest action failed:', response.error)
					setSuggestions([])
				}
			} catch (error) {
				if (currentRequestId !== requestIdRef.current) return
				console.error('Error fetching suggestions:', error)
				setSuggestions([])
			}
		},
		[urlParams.lang, getAutosuggestMode]
	)

	const onSuggestionsFetchRequested = (
		params: SuggestionsFetchRequestedParams
	) => {
		fetchSuggestions(params.value)
	}

	const onDateChange = (dates: Date[]) => {
		if (dates[0]?.getTime() === dates[1]?.getTime()) {
			dates[1] = addDays(dates[1], 1)
		}

		if (dates[0]) {
			setDateFrom(dates[0])
			setOpenedInput(INPUT.CALENDAR_TO)
			if (dates[0] && !dates[1]) {
				emitFromDateGAEvent()
			}
		}

		setDateTo(dates[1] ? dates[1] : null)

		if (dates[1]) {
			if (isDesktop) {
				handleClose()
				setIsDatepickerOpen(false)
				setDatesAreChanged(true)
			}
			emitToDateGAEvent()
		}
	}

	const openCalendar = (input: INPUT) => {
		setTimeout(() => {
			handleOpenInput(input)
		}, 100)

		setIsDatepickerOpen(true)
	}

	const searchInputDisplayValue =
		searchValue?.displayValue || searchValue?.displayName || ''

	const handleCancelDateSelection = () => {
		setDateFrom(previousDateFrom)
		setDateTo(previousDateTo)
		setIsDatepickerOpen(false)
		handleClose()
	}

	const onSuggestionSelected = async (
		_event: FormEvent<any>,
		value: SuggestionSelectedEventData<Suggestion>
	) => {
		setInputError(null)
		const { id, placeID } = value.suggestion
		let selectedForLog: SearchItem | null = null

		if (id) {
			const selected = find(suggestionsResult, { id: id }) || null
			setSelectedItem(selected)
			selectedForLog = selected
		} else {
			try {
				const mappedPlace = await parseGooglePlaceToSelectedItem(placeID)
				setSelectedItem(mappedPlace)
				setSelectedSuggestion(mappedPlace.displayname)
				selectedForLog = mappedPlace
			} catch (error) {
				setInputError(t('locationError'))
				return
			}
		}
		englishLocationNameRef.current = null
		const sourceQuery = (lastFetchQueryRef.current || lastTypedQueryRef.current).trim()
		const isNonAsciiDisplayName = /[^\u0000-\u007F]/.test(
			String(selectedForLog?.displayname || '')
		)
		if (selectedForLog && sourceQuery && isNonAsciiDisplayName) {
			try {
				const countryForLookup = selectedForLog.cc || 'GB'
				const englishLookup = await fetchAutosuggestAction({
					query: sourceQuery,
					locale: 'en',
					language: 'en',
					country: countryForLookup,
					mode: 'new',
				})
				if (englishLookup.success && Array.isArray(englishLookup.data)) {
					const englishCandidates = englishLookup.data as SearchItem[]
					const matched = englishCandidates.find(
						(item) =>
							String(item.placeID || '') ===
							String(selectedForLog?.placeID || '') ||
							String(item.ctid || '') === String(selectedForLog?.ctid || '')
					)
					if (matched?.displayname) {
						englishLocationNameRef.current = matched.displayname
					}
				}
			} catch (lookupError) {
				// noop
			}
		}

		setSelectedSuggestion(value.suggestionValue)
		handleClose()

		if (!datesAreChanged) {
			setTimeout(() => {
				openCalendar(INPUT.CALENDAR_FROM)
			}, 200)
		}
	}

	const onInputChange = (value: string) => {
		setInputError(null)
		lastTypedQueryRef.current = value
		if (value !== selectedSuggestion) {
			// Removing the suggestion when input changes
			setSelectedItem(null)
		}
	}

	const handleRoomsAndGuestsChange = (rooms: Allocation[]) => {
		const roomsCount = rooms.length
		const guestsCount = getGuestsCount(rooms)

		setRoomsAndGuests(rooms)
		setRoomsAndGuestsText(t('roomsAndGuests', { roomsCount, guestsCount }))

		// handleClose()
	}

	async function handleSearch() {
		// smoothScrollTo(0, 0)
		if (!selectedItem) {
			handleLocationError()
			return
		}

		const frontTabRedirectCriteria =
			(isMainPage || isHotelLandingPage || isHotelPage) && hasFrontPartner && !shouldIgnoreFrontTab

		const current = new URLSearchParams(Array.from(searchParams.entries()))
		const existingParams = parseSearchParamsToLower(
			Object.fromEntries(current.entries())
		)

		const allowedPartners = searchParams.get('ap') || null
		const buid = searchParams.get('buid') || null
		const pt = searchParams.get('pt') || null
		const currency =
			searchParams.get('currency') ||
			userData?.selectedCurrency?.abbreviation ||
			userData?.defaultCurrency?.abbreviation ||
			DEFAULT_CURRENCY
		const userCountry = userData?.userCountry

		const {
			country,
			region,
			loctype,
			cityonly,
			name,
			smartyDisplay,
			kayakId: hotelID,
			placeID,
			airportname,
			timezone,
			ctid,
			cc: countryCode,
			googlePlaceId,
			lat,
			lng,
			utcOffsetMinutes,
		} = selectedItem

		const effectiveLoctype =
			selectedItem.kayakType === 'touristregion'
				? SearchItemLocationType.TOURISTREGION
				: loctype
		const dest = buildSearchDestinationPath({
			lang: urlParams.lang as string,
			isHotelPage,
			isSameHotelPageSearch: selectedItem.id === selectedItemRef.current?.id,
			selectedItem,
		})

		// Validate dates before formatting
		if (!validateDateRange(dateFrom, dateTo) || !dateFrom || !dateTo) {
			console.error('Date validation failed')
			Sentry.captureMessage('Date validation failed in handleSearch', {
				level: 'error',
				tags: {
					component: 'Booking Form',
					action: 'handleSearch',
					issue: 'date_validation_failed',
				},
				extra: {
					dateFrom: dateFrom,
					dateTo: dateTo || 'Not Selected',
					maxAllowedDate: addMonths(new Date(), 12),
					selectedItem: selectedItem
						? {
							id: selectedItem.id,
							name: selectedItem.name,
							loctype: selectedItem.loctype,
						}
						: null,
					userAgent: navigator.userAgent,
				},
			})
			return
		}

		const formattedDateFrom = format(dateFrom, DATE_FORMAT_QUERY)
		const formattedDateTo = format(dateTo, DATE_FORMAT_QUERY)

		const rooms = roomsAndGuests.map(
			(room) =>
				`${room.adults}|${room.childAge.map((age) => age.toString()).join(',')}`
		)

		const {
			loctype: paramLoctype,
			entitykey: paramEntityKey,
			slug,
			destcode: paramDestCode,
			locationname,
			placeid: paramPlaceID,
			hotelid: paramHotelID,
			usercountry: paramUserCountry,
			sessionid: paramSessionID,
			timezone: paramTimezone,
			f: frontParam,
			latlon: paramLatLon,
			utcoffsetminutes: paramUtcOffsetMinutes,
			googleplaceid: paramGooglePlaceID,
			page,
			fuid,
			mx: maxPriceParam,
			mn: minPriceParam,
			cal: openCalendarParam,
			...restParams
		} = existingParams || {}

		// This is to remove filters from the results page
		const filteredParams = isMainPage
			? restParams
			: Object.fromEntries(
				Object.entries(restParams).filter(
					([key]) =>
						!['s', 'h', 't', 'gr', 'by', 'dir', 'p', 'fa'].includes(key)
				)
			)

		const urlParamsObject = new URLSearchParams()
		urlParamsObject.set('currency', currency)
		urlParamsObject.set('check-in-date', formattedDateFrom)
		urlParamsObject.set('check-out-date', formattedDateTo)
		urlParamsObject.set('userCountry', userCountry)
		if (lat && lng) {
			urlParamsObject.set('lat', String(lat));
			urlParamsObject.set('lng', String(lng));
			urlParamsObject.set('zoom', String(14));
		}

		const cityName = selectedItem.cityonly && selectedItem.cityonly

		if (effectiveLoctype === SearchItemLocationType.AIRPORT) {
			urlParamsObject.set('locationName', cityName || selectedItem.name)
		} else if (effectiveLoctype === SearchItemLocationType.CITY) {
			urlParamsObject.set('locationName', cityName)
		} else if (effectiveLoctype === SearchItemLocationType.HOTEL) {
			urlParamsObject.set('locationName', selectedItem.name)
		} else if (
			selectedItem.loctype === SearchItemLocationType.REGION ||
			selectedItem.loctype === SearchItemLocationType.COUNTRY ||
			selectedItem.loctype === SearchItemLocationType.FREEREGION ||
			selectedItem.loctype === SearchItemLocationType.TOURISTREGION
		) {
			urlParamsObject.set(
				'locationName',
				startCase(camelCase(selectedItem.name))
			)
		}

		// if (customEntityKey) {
		// 	urlParamsObject.set('entityKey', customEntityKey)
		// }
		if (userData?.sessionID) {
			urlParamsObject.set('sessionID', userData?.sessionID)
		}
		if (timezone) {
			urlParamsObject.set('timezone', timezone)
		}
		if (ctid) {
			urlParamsObject.set('ctid', ctid)
		}
		if (countryCode) {
			urlParamsObject.set('cc', countryCode)
		}
		if (placeID) {
			urlParamsObject.set('placeID', placeID)
		}

		if (
			selectedItem.loctype === SearchItemLocationType.HOTEL &&
			selectedItem.kayakId
		) {
			urlParamsObject.set('hotelID', hotelID)
		}
		if (allowedPartners) {
			urlParamsObject.set('ap', allowedPartners)
		}
		if (googlePlaceId) {
			urlParamsObject.set('googlePlaceId', googlePlaceId)
			urlParamsObject.set('latlon', `${lat},${lng}`)
			urlParamsObject.set('utcOffsetMinutes', String(utcOffsetMinutes))
		}

		rooms.forEach((room) => {
			urlParamsObject.append('room', room)
		})

		Object.entries(filteredParams).forEach(([key, value]) => {
			if (!urlParamsObject.has(key)) {
				urlParamsObject.set(key, value)
			}
		})

		// Add b parameter when compare with checkbox is checked and partner info exists
		if (isCompareChecked && backTabPartner && partnerInfo) {
			urlParamsObject.set('b', backTabPartner)
		}

		const url = `${dest}?${urlParamsObject.toString()}`

		setShowCookieNotice(false)

		emitFormSearchGAEvent({
			dateFrom: formattedDateFrom,
			dateTo: formattedDateTo,
			roomsAndGuests,
			value: smartyDisplay,
		})

		const backTabRedirectCriteria =
			hasBackTabRedirect &&
			(
				isHotelLandingPage ||
				isHotelPage ||
				(isMainPage && ((isCompareChecked && partnerInfo) || urlSearchParams.has('b')))
			)

		const hasExplicitBackAndFrontPartners =
			urlSearchParams.has('b') && urlSearchParams.has('f') && !shouldIgnoreFrontTab

		const hasCityDestcode = destCode?.toLowerCase()?.includes('c:')
		const localDestcode = destCode?.includes(':')
			? destCode.substring(destCode.indexOf(':') + 1)
			: destCode
		const getCommonFormData = () => ({
			...selectedItem,
			dateFrom,
			dateTo,
			roomsAndGuests,
			backTabPartner,
			frontTabPartner,
			pt,
			buid,
			...(hasCityDestcode && { ptid: localDestcode }),
		})

		const handleRedirect = async (
			formType: FORM_TYPE,
			shouldOpenResults: boolean = false
		) => {
			// Using fresh search params from window to avoid any React state sync issues
			const freshParams = new URLSearchParams(window.location.search)
			const freshF = freshParams.get('f')?.toLowerCase()
			const freshFuid = freshParams.get('fuid')
			const freshB = freshParams.get('b')?.toLowerCase()
			const freshBuid = freshParams.get('buid')

			let finalFrontPartner: string | null | undefined = frontTabPartner
			let finalBackPartner: string | null | undefined = backTabPartner
			let finalFuid: string | null | undefined = fuid
			let finalBuid: string | null | undefined = buid

			if (formType === FORM_TYPE.FRONT) {
				const { f: normF, fuid: normFuid } = normalizeFrontPartnerParams(
					freshF || frontTabPartner || undefined,
					freshFuid || fuid || undefined
				)
				finalFrontPartner = normF
				finalFuid = normFuid

				// Pass through back params as is for the link, but don't let them affect front logic
				finalBackPartner = freshB || backTabPartner
				finalBuid = freshBuid || buid
			} else {
				// FORM_TYPE.BACK
				const { b: normB, buid: normBuid } = normalizeBackPartnerParams(
					freshB || backTabPartner || undefined,
					freshBuid || buid || undefined
				)
				finalBackPartner = normB
				finalBuid = normBuid

				// Preserve front params
				finalFrontPartner = freshF || frontTabPartner
				finalFuid = freshFuid || fuid
			}

			let formData = getFormData({
				...getCommonFormData(),
				frontTabPartner: finalFrontPartner,
				backTabPartner: finalBackPartner,
				fuid: finalFuid,
				buid: finalBuid,
				formType,
			})
			// Deleting destination-id as it can be missing for some partners and is not needed for the redirect forms
			delete formData['destination-id'];

			// Backend builds wrong Kayak slug when destination-id+placeID present for airports (INT 404).
			const isKayakFront =
				finalFrontPartner === 'km' ||
				finalFrontPartner === REDIRECT_PARTNERS.KAYAK
			const isKayakBack = finalBackPartner === REDIRECT_PARTNERS.KAYAK
			const isKayakFlow =
				(formType === FORM_TYPE.FRONT && isKayakFront) ||
				(formType === FORM_TYPE.BACK && isKayakBack)
			const isVioFront =
				formType === FORM_TYPE.FRONT && finalFrontPartner === 'vio'
			const isVioBack =
				formType === FORM_TYPE.BACK && finalBackPartner === 'vio'
			const isVioFlow = isVioFront || isVioBack

			const destKey = formData['destination-key']
			const isAirport =
				destKey === 'airport' ||
				effectiveLoctype === SearchItemLocationType.AIRPORT
			const isNonAirportPlace = [
				'city',
				'region',
				'touristregion',
				'town',
				'country',
				'landmark',
				'hood',
				'borough',
			].includes(destKey || '')
			const shouldStripIdsForKayak =
				isKayakFlow && selectedItem && (isAirport || isNonAirportPlace)

			if (shouldStripIdsForKayak) {
				formData = { ...formData }
				if (isAirport && selectedItem?.ap) {
					const cleanName =
						selectedItem.name?.replace(/\s*\([^)]*\)/, '').trim() || ''
					const cleanCity =
						selectedItem.cityonly ||
						selectedItem.cityname?.split(',')[0] ||
						''
					const cleanCountry = selectedItem.country || ''
					const hyphenatedName = cleanName.replace(/\s+/g, '-')
					if (cleanCity && cleanCountry && hyphenatedName) {
						formData['location-name'] =
							`${cleanCity},${cleanCountry},${hyphenatedName}`
					}
					if (selectedItem.placeID && selectedItem.ap) {
						formData['destination-id'] =
							`p${selectedItem.placeID}-l${selectedItem.ap.toUpperCase()}`
					} else {
						formData['destination-id'] = `l${selectedItem.ap.toUpperCase()}`
					}
					delete formData.placeID
				} else if (destKey === 'region' || destKey === 'touristregion') {
					delete formData['destination-id']
					delete formData.placeID
				} else {
					if (selectedItem?.ptid) {
						formData['destination-id'] = `C:${selectedItem.ptid}`
					}
					delete formData.placeID
				}
			}

			const rawFrontPartnerCode = String(
				formData.f || finalFrontPartner || frontParam || ''
			).toLowerCase()
			const rawBackPartnerCode = String(
				formData.b || finalBackPartner || backTabPartner || ''
			).toLowerCase()
			const canonicalFrontPartnerCode =
				rawFrontPartnerCode === 'bb' ? 'b' : rawFrontPartnerCode
			const canonicalBackPartnerCode =
				rawBackPartnerCode === 'bb' ? 'b' : rawBackPartnerCode
			const isExpediaFrontFlow =
				formType === FORM_TYPE.FRONT && canonicalFrontPartnerCode === 'ex'
			const isExpediaBackFlow =
				formType === FORM_TYPE.BACK && canonicalBackPartnerCode === 'ex'
			const isBookingFrontFlow =
				formType === FORM_TYPE.FRONT && canonicalFrontPartnerCode === 'b'
			const isBookingBackFlow =
				formType === FORM_TYPE.BACK && canonicalBackPartnerCode === 'b'
			const isBookingFlowForEnglishFallback =
				isBookingFrontFlow || isBookingBackFlow
			const isExpediaFlowForEnglishFallback =
				isExpediaFrontFlow || isExpediaBackFlow
			const shouldUseEnglishFallback =
				(isVioFlow ||
					isExpediaFlowForEnglishFallback ||
					(isBookingFlowForEnglishFallback && hasCityDestcode)) &&
				String(urlParams.lang || '').toLowerCase() !== 'en'
			let englishLocationName = englishLocationNameRef.current
			if (shouldUseEnglishFallback && !englishLocationName && selectedItem) {
				const sourceQuery = (
					lastFetchQueryRef.current ||
					lastTypedQueryRef.current ||
					selectedItem.displayname ||
					selectedItem.name ||
					''
				).trim()
				const countryForLookup = selectedItem.cc || 'GB'
				if (sourceQuery) {
					try {
						const englishLookup = await fetchAutosuggestAction({
							query: sourceQuery,
							locale: 'en',
							language: 'en',
							country: countryForLookup,
							mode: 'new',
						})
						if (englishLookup.success && Array.isArray(englishLookup.data)) {
							const englishCandidates = englishLookup.data as SearchItem[]
							const destcodeId = hasCityDestcode
								? String(localDestcode || '')
								: ''
							let matchedBy = 'none'
							let matched = englishCandidates.find((item) => {
								const placeMatch =
									String(item.placeID || '') ===
									String(selectedItem.placeID || '') &&
									String(selectedItem.placeID || '').length > 0
								if (placeMatch) {
									matchedBy = 'selected-placeID'
									return true
								}
								const ctidMatch =
									String(item.ctid || '') === String(selectedItem.ctid || '') &&
									String(selectedItem.ctid || '').length > 0
								if (ctidMatch) {
									matchedBy = 'selected-ctid'
									return true
								}
								const destcodeCtidMatch =
									String(item.ctid || '') === destcodeId &&
									destcodeId.length > 0
								if (destcodeCtidMatch) {
									matchedBy = 'destcode-ctid'
									return true
								}
								const destcodePlaceMatch =
									String(item.placeID || '') === destcodeId &&
									destcodeId.length > 0
								if (destcodePlaceMatch) {
									matchedBy = 'destcode-placeID'
									return true
								}
								return false
							})

							if (!matched && isExpediaFlowForEnglishFallback && englishCandidates.length > 0) {
								matched = englishCandidates[0]
								matchedBy = 'expedia-first-candidate'
							}

							englishLocationName = matched?.displayname || null

							if (englishLocationName) {
								englishLocationNameRef.current = englishLocationName
							}
						}
					} catch (lookupError) {
						// noop
					}
				}
			}

			if (shouldUseEnglishFallback && englishLocationName) {
				const englishParts = englishLocationName
					.split(',')
					.map((part) => part.trim())
					.filter(Boolean)
				const englishCity = englishParts[0] || null
				const englishCountry = englishParts[englishParts.length - 1] || null
				const expediaVicinityCity = englishCity
					? `${englishCity} (and vicinity)`
					: null
				const locationNameBefore = String(formData['location-name'] || '')
				const cityBefore = String(formData.city || '')
				const countryBefore = String(formData['hotel-country'] || '')
				const destinationBefore = String(formData.destination || '')
				const shouldUseExpediaCityVicinity =
					isExpediaFlowForEnglishFallback &&
					formData['destination-key'] === 'city' &&
					!hasCityDestcode &&
					!selectedItem?.placeID &&
					!selectedItem?.ctid
				const targetEnglishLocationName = shouldUseExpediaCityVicinity && expediaVicinityCity && englishCountry
					? `${expediaVicinityCity}, ${englishCountry}`
					: englishLocationName

				formData = {
					...formData,
					'location-name': targetEnglishLocationName,
					...(isExpediaFlowForEnglishFallback && englishCity && {
						city: shouldUseExpediaCityVicinity && expediaVicinityCity
							? expediaVicinityCity
							: englishCity,
					}),
					...(isExpediaFlowForEnglishFallback && englishCountry && { 'hotel-country': englishCountry }),
					...(isExpediaFlowForEnglishFallback && { destination: targetEnglishLocationName }),
				}

			}
			// Booking backend expects canonical partner code in back flow.
			// bb works in front passthru context, but for tab=back it resolves to Booking homepage.
			if (formType === FORM_TYPE.BACK && formData.b === 'bb') {
				formData = {
					...formData,
					b: 'b',
				}
			}
			// Booking backend also expects canonical code for front flow.
			// With f=bb it redirects to Booking homepage, while f=b returns search results URL.
			if (formType === FORM_TYPE.FRONT && formData.f === 'bb') {
				formData = {
					...formData,
					f: 'b',
				}
			}
			if (isBookingFlowForEnglishFallback && formData['destination-key'] === 'city') {
				const locationNameCurrent = String(formData['location-name'] || '').trim()
				const destinationCurrent = String(formData.destination || '').trim()
				const cityCurrent = String(formData.city || '').trim()
				const countryCurrent = String(formData['hotel-country'] || '').trim()
				const locationParts = locationNameCurrent
					.split(',')
					.map((part) => part.trim())
					.filter(Boolean)
				const cityCountryDuplicate =
					locationParts.length >= 2 &&
					locationParts[0].toLowerCase() === locationParts[1].toLowerCase()
				const bookingVariantLocationName = cityCountryDuplicate ? locationParts[0] : locationNameCurrent
				const bookingVariantDestination = cityCountryDuplicate ? locationParts[0] : destinationCurrent
				if (cityCountryDuplicate) {
					formData = {
						...formData,
						...(bookingVariantLocationName && { 'location-name': bookingVariantLocationName }),
						...(bookingVariantDestination && { destination: bookingVariantDestination }),
					}
				}
			}
			if (formType === FORM_TYPE.FRONT && (formData.b || formData.buid)) {
				const bBefore = formData.b || null
				const buidBefore = formData.buid || null
				formData = { ...formData }
				delete formData.b
				delete formData.buid
			}
			const normalizedDestCode = typeof destCode === 'string' ? destCode.trim().toUpperCase() : ''
			const isExpediaFlowForCityDestcode =
				(formType === FORM_TYPE.FRONT && formData.f === 'ex') ||
				(formType === FORM_TYPE.BACK && formData.b === 'ex')
			if (formData['destination-key'] === 'city' && hasCityDestcode && localDestcode) {
				const destinationIdBefore = String(formData['destination-id'] || '')
				formData = {
					...formData,
					'destination-id': `C:${localDestcode}`,
				}
			}
			if (
				isExpediaFlowForCityDestcode &&
				formData['destination-key'] === 'city' &&
				shouldUseEnglishFallback &&
				hasCityDestcode
			) {
				const destinationIdBefore = String(formData['destination-id'] || '')
				formData = { ...formData }
				delete formData['destination-id']
				delete formData.placeID
			}
			const isExpediaBack = formType === FORM_TYPE.BACK && formData.b === 'ex'
			const isExpediaFront = formType === FORM_TYPE.FRONT && formData.f === 'ex'
			const isExpediaFlow = isExpediaBack || isExpediaFront
			if (isExpediaFlow && formData['destination-key'] === 'touristregion') {
				const cityBefore = String(formData.city || '')
				const normalizedRegionName = String(formData['location-name'] || '')
					.split(',')[0]
					.trim()
				if (normalizedRegionName) {
					formData = {
						...formData,
						city: normalizedRegionName,
					}
				}
			}
			const hasIataDestCode = /^[A-Z]{3}$/.test(normalizedDestCode)
			if (formData['destination-key'] === 'airport' && !formData['destination-id'] && hasIataDestCode) {
				const isKayakAirportFallback =
					(formType === FORM_TYPE.FRONT &&
						(finalFrontPartner === 'km' ||
							finalFrontPartner === REDIRECT_PARTNERS.KAYAK)) ||
					(formType === FORM_TYPE.BACK &&
						finalBackPartner === REDIRECT_PARTNERS.KAYAK)
				const normalizedFallbackDestinationId = isKayakAirportFallback
					? `l${normalizedDestCode}`
					: normalizedDestCode
				formData = {
					...formData,
					'destination-id': normalizedFallbackDestinationId,
				}
			}
			if (formData['destination-key'] === 'airport' && hasIataDestCode) {
				const rawLocationName = String(formData['location-name'] || '')
				const locationParts = rawLocationName
					.split(',')
					.map((part) => part.trim())
					.filter(Boolean)
				const cityPart = locationParts[1] || String(formData.city || '').trim()
				const countryPart = locationParts[2] || String(formData['hotel-country'] || '').trim()
				const airportLocationName = [normalizedDestCode, cityPart, countryPart]
					.filter(Boolean)
					.join(',')

				formData = {
					...formData,
					'location-name': airportLocationName || rawLocationName,
					...(cityPart && { city: cityPart }),
				}
			}
			if (formType === FORM_TYPE.BACK) {
				if (shouldOpenResults) {
					window.open(url, '_blank', 'noopener,noreferrer')
				}
				submitHiddenForm(formData, redirectFormRef.current as HTMLFormElement)
			} else if (formType === FORM_TYPE.FRONT) {
				const isKayakMetaRedirectFlow = isKayakMetaFlow(
					finalFrontPartner,
					finalFuid
				)
				const redirectUrl = buildRedirectUrl(
					urlParams.lang as string,
					isKayakMetaRedirectFlow,
					formData
				)
				window.open(redirectUrl, '_blank', 'noopener,noreferrer')
				if (shouldOpenResults) {
					router.push(url)
				}
				setFrontFormData(formData)
			}
		}

		// Determine if exactly one criteria is met
		// If both b and f are explicitly provided, do not navigate to results here.
		const shouldOpenResultsPage = hasExplicitBackAndFrontPartners
			? false
			: frontTabRedirectCriteria !== backTabRedirectCriteria

		if (frontTabRedirectCriteria) {
			await handleRedirect(FORM_TYPE.FRONT, shouldOpenResultsPage)
		}

		if (backTabRedirectCriteria) {
			const openFrontTabResults =
				!hasExplicitBackAndFrontPartners && shouldOpenResultsPage && !shouldIgnoreFrontTab
			await handleRedirect(FORM_TYPE.BACK, openFrontTabResults)
		}

		// Only redirect to results page if neither criteria is met
		if (!backTabRedirectCriteria && !frontTabRedirectCriteria) {
			router.push(url)
		}
	}

	const handleUserEmailChange = (value: string) => {
		setUserEmail(value)
		setEmailError('')
	}
	const handleLocationError = () => {
		onInputFocus()
		if (!isMobile) {
			setTimeout(() => {
				setInputError(t('locationError'))
			}, 100)
		}

		emitSearchWithoutLocationGAEvent()
	}

	const handleSignupAndSearch = (email: string) => {
		if (!selectedItem) {
			handleLocationError()
			return
		}

		if (email.length) {
			const validEmail = validateEmail(email)

			if (!validEmail) {
				setEmailError(t('emailError'))
				return
			}
		}

		if (!email) {
			emitFormEmailSubscribeBounceGAEvent()
			setIsOpenEmailSignup(false)
			handleSearch()
			return
		}

		const buid = searchParams.get('buid') || null
		const fuid = searchParams.get('fuid') || null
		const pt = searchParams.get('pt') || null
		const hasCityDestcode = destCode?.toLowerCase()?.includes('c:')

		const formData = getFormData({
			...selectedItem,
			dateFrom,
			dateTo,
			roomsAndGuests,
			buid,
			fuid,
			pt,
			...(hasCityDestcode && { ptid: destCode }),
		})

		const {
			_adults,
			_children,
			'destination-id': _,
			...restFormData
		} = formData
		const { placeID, kayakId: hotelID, kayakType } = selectedItem
		const isHotel = selectedItem.loctype === SearchItemLocationType.HOTEL
		const currency =
			searchParams.get('currency') ||
			userData?.selectedCurrency?.abbreviation ||
			userData.defaultCurrency.abbreviation

		const subscriptionData: PriceAlertSubscriptionData = {
			...(restFormData as Omit<
				PriceAlertSubscriptionData,
				'email' | 'adults' | 'children' | 'lng' | 'currency'
			>),
			email: email,
			adults: formData._adults,
			children: formData._children,
			lng: urlParams.lang as string,
			currency: currency,
			'destination-id': placeID || formData['destination-id'],
		}

		priceAlertSubscription(subscriptionData)
			.then(() => {
				console.log('subscription successful')
				emitFormEmailSubmitGAEvent()
			})
			.catch((error) => {
				console.error('An error occurred during subscription:', error)
			})
			.finally(() => {
				setUserData({ ...userData, email })
				handleSearch()
			})
	}

	const handleOpenEmailDialog = () => {
		if (authData?.user?.email) {
			handleSignupAndSearch(authData?.user?.email)
			return
		}

		if (!selectedItem) {
			if (isMobile) {
			} else {
				onInputFocus()
				setInputError(t('locationError'))
			}

			return
		}
		setIsOpenEmailSignup(true)
	}

	const onCalendarClose = () => {
		if (dateFrom && dateTo === null) {
			const suggestedCheckout = addDays(dateFrom, 1)
			const maxDate = addMonths(new Date(), 12)

			if (suggestedCheckout <= maxDate) {
				setDateTo(suggestedCheckout)
			}
		}

		setIsDatepickerOpen(false)
		handleClose()
	}

	const onRoomsAndGuestsOpen = () => {
		handleOpenInput(INPUT.GUESTS_SELECT)
		emitGuestsClickGAEvent()
	}

	const onRoomsAndGuestsClose = () => {
		if (!isDesktop) {
			smoothScrollTo(0, 0)
		}

		handleClose()

		emitGuestsSelectGAEvent({
			rooms: roomsAndGuests.length,
			adults: roomsAndGuests.reduce((acc, room) => acc + room.adults, 0),
			children: roomsAndGuests.reduce(
				(acc, room) => acc + room.childAge.length,
				0
			),
			total: getGuestsCount(roomsAndGuests),
		})
	}

	const handleApplyGuestsAndSearch = () => {
		handleOpenInput(null)
		handleSearch()
	}

	useEffect(() => {
		if (isMobile && isDatepickerOpen) {
			setTimeout(() => scrollToSelectedMonth(dateFrom as Date), 0)
		}
	}, [isDatepickerOpen])

	const handleApplyDates = () => {
		setIsDatepickerOpen(false)
		setPreviousDateFrom(dateFrom)
		setPreviousDateTo(dateTo)
		eventBus.publish(PS_EVENTS.INPUT_FOCUS, INPUT.MAIN_FORM)
	}

	const handleOpenInput = (input: AvailableInputTypes) => {
		if (openedInput === INPUT.LOCATION && input === null) {
			smoothScrollTo(0, 0)
		}

		setInputError(null)
		setOpenedInput(input)

		if (smHeight && isDesktop) {
			bookingRef.current?.scrollIntoView({
				behavior: 'smooth',
				block: 'start',
			})
		}
	}

	const handleClose = () => {
		setOpenedInput(null)
		if (!isDesktop) {
			eventBus.publish(PS_EVENTS.INPUT_FOCUS, INPUT.MAIN_FORM)
		}
		document.body.style.overflowY = 'initial'
	}

	const onInputFocus = (input: INPUT = INPUT.LOCATION) => {
		const isLocationInput = input === INPUT.LOCATION
		handleOpenInput(input)

		if (isLocationInput) {
			bookingInputDestRef.current?.focus()
		}

		if (!isDesktop) {
			document.body.style.overflowY = 'hidden'
		}
	}

	return (
		<div
			className={classNames('booking__form', {
				'max-lg:pt-6 max-lg:border border-text-300 md:max-lg:mb-5 md:max-lg:mx-5':
					isMainPage,
			})}
		>
			{isMainPage && mobileHeadingSlot && (
				<div className="lg:hidden text-center mb-4">
					{mobileHeadingSlot}
				</div>
			)}

			{/* Anchor (autoscroll) */}
			<div className="relative" style={{ top: '-110px' }} ref={bookingRef} />
			<div
				ref={bookingInputsRef}
				className={classNames(
					'booking w-full flex lg:mt-8 justify-center flex-wrap mx-auto',
					{ 'max-lg:px-3 xl:max-w-[1048px] lg:max-xl:container': isMainPage },
					{ 'max-w-[1048px]': !isMainPage }
				)}
			>
				<form
					ref={redirectFormRef}
					id="redirect-form"
					method="post"
					action={process.env.NEXT_PUBLIC_BACKEND_URL + '/hotels/redirect'}
				/>
				<form
					ref={redirectFormBlankRef}
					id="redirect-form_blank"
					method="post"
					target="_blank"
					action={process.env.NEXT_PUBLIC_BACKEND_URL + '/hotels/redirect'}
				/>
				<div className="booking__wrapper">
					<div
						className={classNames(
							'flex-1 w-full lg:h-full md:max-lg:mb-2 max-lg:mb-3',
							{
								'lg:my-0': isMainPage,
							}
						)}
					>
						<SearchAutosuggest
							ref={bookingInputDestRef}
							defaultValue={searchInputDisplayValue}
							suggestions={suggestions}
							inputError={inputError}
							onSuggestionsFetchRequested={onSuggestionsFetchRequested}
							onSuggestionSelected={onSuggestionSelected}
							onInputChange={onInputChange}
							onInputFocus={onInputFocus}
							onInputBlur={handleClose}
							inputClassName={classNames('search-autosuggest__input', {
								'search-autosuggest__input--is-open new-autosuggest-input--open':
									openedInput === INPUT.LOCATION,
							})}
							lang={urlParams.lang as string}
							pageType={
								isMainPage
									? PAGE_TYPE.MAIN_PAGE
									: isHotelPage
										? PAGE_TYPE.HOTEL_PAGE
										: PAGE_TYPE.RESULTS_PAGE
							}
						/>
					</div>
					<BookingDatepicker
						dateFrom={dateFrom}
						dateTo={dateTo}
						isDatepickerOpen={isDatepickerOpen}
						openedInput={openedInput}
						isMobile={!isDesktop}
						isDesktop={isDesktop}
						locale={locale}
						onDateChange={onDateChange}
						onCalendarClose={onCalendarClose}
						openCalendar={openCalendar}
						handleCancelDateSelection={handleCancelDateSelection}
						handleApplyDates={handleApplyDates}
					/>

					<div ref={roomsInputRef} className="relative -top-2"></div>
					<div
						className={classNames(
							'booking__guests-input booking__additional-input',
							{
								'guests-open !border-text-700':
									openedInput === INPUT.GUESTS_SELECT,
							}
						)}
						id={INPUT.GUESTS_SELECT}
						data-test-id="GUESTS_SELECT"
					>
						<FormInput
							id={'rooms-and-guests-input'}
							onClick={onRoomsAndGuestsOpen}
							value={roomsAndGuestsText}
							className={classNames('outline-none max-lg:h-14 rounded-full', {
								'rooms-and-guests-input--is-open':
									openedInput === INPUT.GUESTS_SELECT,
							})}
							readonly
							ref={bookingInputGuestsRef}
						>
							<Icons.iconGuests />
						</FormInput>
						{!isDesktop ? (
							<RemoveScroll
								enabled={isMobile && openedInput === INPUT.GUESTS_SELECT}
								removeScrollBar={false}
							>
								<FullScreenDrawer
									isOpen={openedInput === INPUT.GUESTS_SELECT}
									onClose={onRoomsAndGuestsClose}
									headerText={t('roomsAndGuestsLabel')}
								>
									<GuestsSelect
										onApplyGuestsAndSearch={handleApplyGuestsAndSearch}
										onChange={handleRoomsAndGuestsChange}
										initialValue={roomsAndGuests}
										onClose={onRoomsAndGuestsClose}
										isMobile={true}
										isMainPage={isMainPage as boolean}
									/>
								</FullScreenDrawer>
							</RemoveScroll>
						) : (
							<Popup
								className="rounded-lg right-0 top-0 lg:top-[68px] z-[70]"
								isOpen={openedInput === INPUT.GUESTS_SELECT}
								onClose={onRoomsAndGuestsClose}
							>
								<GuestsSelect
									onApplyGuestsAndSearch={handleApplyGuestsAndSearch}
									onChange={handleRoomsAndGuestsChange}
									initialValue={roomsAndGuests}
									onClose={onRoomsAndGuestsClose}
									isMobile={false}
									isMainPage={isMainPage as boolean}
								/>
							</Popup>
						)}
					</div>
					<div
						data-test="search-button"
						className="hidden lg:block lg:order-2 xl:order-none"
					>
						<Button
							id="search-button"
							bold
							textColor="white"
							onClick={authData?.user?.email ? handleOpenEmailDialog : handleSearch}
							name="Search"
							className={classNames(
								'!bg-button-main text-xl h-14 hover:opacity-80 !rounded-full transition-all ease-linear w-fit flex items-center justify-center'
							)}
						>
							<Icons.iconLens className="xl:mr-3" />
							<span className="hidden xl:inline">
								{t('newDesign.searchButton')}
							</span>
						</Button>
					</div>
				</div>

				{!isDesktop && isMainPage && (
					<>
						{shouldShowComparisonCheckbox && (
							<div className="price-alert-switch-mobile my-6 lg:mt-0 w-full flex justify-start max-lg:mb-0">
								<Checkbox
									labelLeft={checkboxText.leftLabel}
									labelRight={checkboxText.rightLabel}
									checked={isCompareChecked}
									onChange={setIsCompareChecked}
									testId="compare-with-booking"
									variant="main-dark"
									bold
								/>
							</div>
						)}
					</>
				)}
				<div
					data-test="search-button"
					className="block lg:hidden w-full max-lg:mt-6 lg:px-2 lg:order-3 xl:order-none"
				>
					<Button
						id="search-button"
						big
						bgColor="main"
						textColor="white"
						onClick={
							authData?.user?.email
								? () => handleSignupAndSearch(authData?.user?.email! || userEmail)
								: handleSearch
						}
						name="Search"
						className={classNames(
							'font-bold text-xl !bg-button-main h-14 !rounded-full transition-all ease-linear flex items-center justify-center',
							{
								'mt-3': !isMainPage,
							}
						)}
					>
						<Icons.iconLens className="mr-3" />

						{isMd && !isDesktop
							? t('newDesign.searchButtonMobile')
							: t('newDesign.searchButton')}
					</Button>
				</div>
				<div
					className={classNames(
						'hidden lg:order-2 xl:order-none w-full xl:max-w-[1040px]',
						{
							hidden: !isMainPage,
							'lg:flex ': isMainPage,
						}
					)}
				>
					<div
						className={classNames(
							' flex items-center justify-between mt-4 w-full ps-2 lg:ps-5 pe-2 lg:pe-5 mb-4 lg:mb-0',
							{
								'lg:!justify-start': shouldHideComparisonCheckbox,
							}
						)}
					>
						<div className={classNames(
							"price-alert-switch",
							{
								'invisible pointer-events-none': shouldHideComparisonCheckbox
							}
						)}>
							{checkboxText && (
								<Checkbox
									labelLeft={checkboxText.leftLabel}
									labelRight={checkboxText.rightLabel}
									checked={isCompareChecked}
									onChange={setIsCompareChecked}
									testId="compare-with-booking"
									variant="white"
									bold
								/>
							)}
						</div>
					</div>
				</div>
			</div>
			{isOpenEmailSignup && selectedItem && isDesktop && (
				<EmailSignup
					destination={selectedItem as SearchItem}
					startDate={dateFrom as Date}
					endDate={dateTo as Date}
					isOpen={true}
					onSubmit={handleSignupAndSearch}
					guestCount={getGuestsCount(roomsAndGuests)}
				/>
			)}
		</div>
	)
}

export default dynamic(() => Promise.resolve(Booking), {
	ssr: false,
	suspense: true,
	loading: () => (
		<>
			<div className="home-page-placeholder">
				<BookingPlaceholder />
			</div>
			<div className="results-page-placeholder">{/* <TopLoading /> */}</div>
		</>
	),
})
